import { PostTypes } from 'constants/postTypes'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { TabViewWithFilters } from 'partials/TabViewWithFilters'
import { UpspaceHeader } from 'partials/UpspaceHeader'
import { PostsGridView } from 'partials/UpspaceTabs/widgets/PostsGridView'

import { Modal } from 'widgets/Modal'
import { OptionItem } from 'widgets/OptionsDropdown/subcomponents/OptionItem'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useGroup } from 'hooks/useGroup'
import { useGroupEvents } from 'hooks/useGroupEvents'
import { useHasPremiumAccess } from 'hooks/useHasPremiumAccess'
import { useInfinitePosts } from 'hooks/useInfinitePosts'
import { useUpspaceRoom } from 'hooks/useUpspaceRoom'

import { openReportForm } from 'utils/links/links'

import { useFocusMode } from 'contexts/FocusModeContext'

import { Post, PostFilter } from 'types/post'
import { UserType } from 'types/user'

import { EventList } from '../EventList'

type UpspaceTabsProps = {
  user: UserType
  params: any
  onShare: () => void
  onBackPress: () => void
  onOptionsMenuPress: () => void
  setIsCommentLoading: (isCommentLoading: boolean) => void
}

const postFilters: PostFilter[] = ['All posts']

export const UpspaceTabs = ({
  user,
  params,
  onShare,
  onBackPress,
  onOptionsMenuPress,
  setIsCommentLoading,
}: UpspaceTabsProps) => {
  const focusMode = useFocusMode()
  const isFocusMode = focusMode.group && user && focusMode.group.trainer.id === user.id

  const { data: currentUser } = useCurrentUser()
  const currentUserId = currentUser?.id
  const { data: group } = useGroup({ userId: user.id })
  const canSeeChat =
    useHasPremiumAccess({
      programId: group?.id,
      userId: currentUserId,
    }) || group?.trainer.id === currentUserId

  const { data: room } = useUpspaceRoom({ program: group, userId: currentUserId })

  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)

  const {
    data: posts,
    isLoading: isPostsLoading,
    fetchNextPage: fetchNextPosts,
  } = useInfinitePosts({
    creatorId: user?.id,
    filter: postFilters[0],
    shouldIncludeDrafts: user.id === currentUserId,
  })

  const filteredPosts = useRef<Array<Post>>([])

  useEffect(() => {
    filteredPosts.current = posts
      .filter(
        (p) =>
          (p.isPending === false || p.isPending === undefined) &&
          (p.isArchived === false || p.isArchived === undefined),
      )
      .filter((item) => item.hasSteps || item.type === PostTypes.WORKOUT)
  }, [posts, isPostsLoading])

  const {
    data: groupEventsData,
    isLoading: isEventsLoading,
    fetchNextPage: fetchNextGroupEvents,
  } = useGroupEvents({
    groupIds: [group?.id] as string[],
  })

  const fetchNextEvents = () => {
    setIsFetchingNextPage(true)
    fetchNextGroupEvents()
  }

  const groupEvents = useMemo(
    () => groupEventsData?.pages.reduce((acc, page) => acc.concat(page), []) ?? [],
    [groupEventsData?.pages],
  )

  const events = useMemo(() => {
    let finalEvents = groupEvents.filter(
      (event) =>
        (event.eventType === 'create' || event.eventType === 'completion') &&
        !event.post?.isArchived &&
        !event.post?.isPending,
    )

    const uniqueIds: string[] = []
    finalEvents = finalEvents.filter((event) => {
      const isDuplicate = uniqueIds.includes(event.id)

      if (!isDuplicate) {
        uniqueIds.push(event.id)

        return true
      }

      return false
    })

    finalEvents.sort((a, b) => {
      return b.created - a.created
    })

    setIsFetchingNextPage(false)
    return finalEvents
  }, [groupEvents])

  const menuOptions = ['Report message']
  const [isOptionsModalVisible, setIsOptionsModalVisible] = useState(false)
  const [reportedAccount] = useState<UserType | undefined>()

  const handleCloseOptionsMenuPress = (): void => {
    setIsOptionsModalVisible(false)
  }

  const handleOnSelect = (index: number) => {
    setIsOptionsModalVisible(false)
    if (index === 0) {
      if (room?.avatarUsers.length === 2) {
        openReportForm(room?.avatarUsers[0], room?.avatarUsers[1])
      } else {
        openReportForm(room?.avatarUsers[0], reportedAccount !== undefined ? reportedAccount : null)
      }
    }
  }

  const tabMap: Record<string, ReactNode> = {
    Feed: (
      <EventList
        events={
          !isFocusMode
            ? events
            : events?.filter((event) => event.creator?.id === focusMode?.group?.trainer.id)
        }
        fetchNextEvents={fetchNextEvents}
        loading={isEventsLoading}
        noContentMessage='There have been no posts in this program yet.'
        setIsCommentLoading={setIsCommentLoading}
        isFetchingNextPage={isFetchingNextPage}
      />
    ),
    PostsAndWorkouts: (
      <PostsGridView
        isLoading={isPostsLoading}
        onEndReached={fetchNextPosts}
        posts={
          !isFocusMode
            ? filteredPosts.current
            : filteredPosts.current.filter(
                (post) => post.program?.id === group?.id || post.programIds?.includes(group?.id),
              )
        }
        columns={3}
      />
    ),
  }

  return (
    <>
      <TabViewWithFilters
        scrollable
        withIconTabHeaders
        tabs={canSeeChat ? ['feed', 'grid'] : ['feed', 'grid', 'chat']}
        hideActionsTabs={['Chat', 'Premium', 'Profile']}
        header={
          <UpspaceHeader
            user={user}
            program={group}
            params={{ workouts: filteredPosts.current, ...params }}
            onShare={onShare}
            onBackPress={onBackPress}
            onOptionsMenuPress={onOptionsMenuPress}
          />
        }
      >
        {canSeeChat
          ? Object.values(tabMap)
          : Object.entries(tabMap)
              .filter(([key]) => key !== 'Chat')
              .map(([key, value]) => value)}
      </TabViewWithFilters>

      <Modal
        isVisible={isOptionsModalVisible}
        onClose={handleCloseOptionsMenuPress}
        variant='fullscreenModal'
      >
        {menuOptions.map((option, index) => {
          return (
            <OptionItem
              key={option}
              option={option}
              isCancelItem={false}
              onSelect={() => {
                handleOnSelect(index)
              }}
            />
          )
        })}
        <OptionItem isCancelItem onSelect={handleCloseOptionsMenuPress} />
      </Modal>
    </>
  )
}
